import { flushSync } from 'react-dom';

import { prefersReducedMotion } from './motion';

type StartViewTransition = (update: () => void) => { finished?: Promise<unknown> };

/**
 * Runs a page-wide change inside a View Transition when the browser has them
 * and the person did not ask for less motion; otherwise just runs it. React
 * commits inside the update (flushSync), so the new snapshot is the new page.
 * `reveal` turns the default crossfade into a circle growing from that point
 * (Día/Noche from the moon button).
 */
export function withViewTransition(update: () => void, reveal?: { x: number; y: number }): void {
  const start = (document as Document & { startViewTransition?: StartViewTransition }).startViewTransition;
  if (typeof start !== 'function' || prefersReducedMotion()) {
    update();
    return;
  }
  const root = document.documentElement;
  if (reveal) {
    root.style.setProperty('--reveal-x', `${reveal.x}px`);
    root.style.setProperty('--reveal-y', `${reveal.y}px`);
    root.dataset.reveal = '';
  }
  const transition = start.call(document, () => flushSync(update));
  void transition.finished?.finally(() => {
    delete root.dataset.reveal;
  });
}

/** The center of the element that was tapped, where a reveal starts. */
export function centerOf(element: Element): { x: number; y: number } {
  const box = element.getBoundingClientRect();
  return { x: box.left + box.width / 2, y: box.top + box.height / 2 };
}
